// src/services/userAnalyticsService.js
// Service for fetching real user analytics data from Supabase

import { supabase } from '../config';

const UserAnalyticsService = {
  /**
   * Fetch user statistics
   * Returns: totalUsers, newUsers, activeUsers, retentionRate
   */
  async fetchUserStats() {
    try {
      // Get total users
      const { count: totalUsers, error: totalError } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });

      if (totalError) throw totalError;

      // Get new users this month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: newUsers, error: newUsersError } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString());

      if (newUsersError) throw newUsersError;

      // Get active users (users with activity in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Check equity_logs for activity
      const { data: equityActiveUsers } = await supabase
        .from('equity_logs')
        .select('user_id')
        .gte('timestamp', thirtyDaysAgo.toISOString());

      // Check dataroom_logs for activity
      const { data: dataroomActiveUsers } = await supabase
        .from('dataroom_logs')
        .select('user_id')
        .gte('timestamp', thirtyDaysAgo.toISOString());

      // Combine and get unique active users
      const activeUserIds = new Set([
        ...(equityActiveUsers || []).map(u => u.user_id),
        ...(dataroomActiveUsers || []).map(u => u.user_id)
      ]);

      const activeUsers = activeUserIds.size;

      return {
        success: true,
        data: {
          totalUsers: totalUsers || 0,
          newUsers: newUsers || 0,
          activeUsers: activeUsers
        }
      };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      return {
        success: false,
        error: error.message,
        data: {
          totalUsers: 0,
          newUsers: 0,
          activeUsers: 0
        }
      };
    }
  },

  /**
   * Fetch activity statistics
   * Returns: dailyActive, weeklyActive, monthlyActive, avgSessionTime
   */
  async fetchActivityStats() {
    try {
      const now = new Date();
      
      // Daily active (last 24 hours)
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      
      // Weekly active (last 7 days)
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      // Monthly active (last 30 days)
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Get daily active users from logs
      const [equityDaily, dataroomDaily] = await Promise.all([
        supabase
          .from('equity_logs')
          .select('user_id')
          .gte('timestamp', oneDayAgo.toISOString()),
        supabase
          .from('dataroom_logs')
          .select('user_id')
          .gte('timestamp', oneDayAgo.toISOString())
      ]);

      const dailyActiveIds = new Set([
        ...(equityDaily.data || []).map(u => u.user_id),
        ...(dataroomDaily.data || []).map(u => u.user_id)
      ]);

      // Get weekly active users
      const [equityWeekly, dataroomWeekly] = await Promise.all([
        supabase
          .from('equity_logs')
          .select('user_id')
          .gte('timestamp', sevenDaysAgo.toISOString()),
        supabase
          .from('dataroom_logs')
          .select('user_id')
          .gte('timestamp', sevenDaysAgo.toISOString())
      ]);

      const weeklyActiveIds = new Set([
        ...(equityWeekly.data || []).map(u => u.user_id),
        ...(dataroomWeekly.data || []).map(u => u.user_id)
      ]);

      // Get monthly active users
      const [equityMonthly, dataroomMonthly] = await Promise.all([
        supabase
          .from('equity_logs')
          .select('user_id')
          .gte('timestamp', thirtyDaysAgo.toISOString()),
        supabase
          .from('dataroom_logs')
          .select('user_id')
          .gte('timestamp', thirtyDaysAgo.toISOString())
      ]);

      const monthlyActiveIds = new Set([
        ...(equityMonthly.data || []).map(u => u.user_id),
        ...(dataroomMonthly.data || []).map(u => u.user_id)
      ]);

      return {
        success: true,
        data: {
          dailyActive: dailyActiveIds.size,
          weeklyActive: weeklyActiveIds.size,
          monthlyActive: monthlyActiveIds.size
        }
      };
    } catch (error) {
      console.error('Error fetching activity stats:', error);
      return {
        success: false,
        error: error.message,
        data: {
          dailyActive: 0,
          weeklyActive: 0,
          monthlyActive: 0
        }
      };
    }
  },


  /**
   * Fetch feature usage statistics
   * Returns: usage stats for each product (equity, dataroom, capraise, questions)
   */
  async fetchFeatureUsage() {
    try {
      // Get users with product roles
      const { data: productRoles } = await supabase
        .from('user_product_roles')
        .select('product_name, user_id');

      if (!productRoles) {
        return {
          success: true,
          data: {
            equity: { active: 0, total: 0 },
            dataroom: { active: 0, total: 0 },
            capraise: { active: 0, total: 0 },
            questions: { active: 0, total: 0 }
          }
        };
      }

      // Count total users per product
      const productTotals = {};
      productRoles.forEach(role => {
        if (!productTotals[role.product_name]) {
          productTotals[role.product_name] = new Set();
        }
        productTotals[role.product_name].add(role.user_id);
      });

      // Get active users (users with activity in last 30 days) per product
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Equity active users
      const { data: equityActive } = await supabase
        .from('equity_logs')
        .select('user_id')
        .gte('timestamp', thirtyDaysAgo.toISOString());

      // Dataroom active users
      const { data: dataroomActive } = await supabase
        .from('dataroom_logs')
        .select('user_id')
        .gte('timestamp', thirtyDaysAgo.toISOString());

      // CapRaise active users (if capraise_activities table exists)
      let capraiseActive = [];
      try {
        const { data } = await supabase
          .from('capraise_activities')
          .select('user_id')
          .gte('created_at', thirtyDaysAgo.toISOString());
        capraiseActive = data || [];
      } catch (e) {
        // Table might not exist
        console.log('capraise_activities table not found');
      }

      const equityActiveSet = new Set((equityActive || []).map(u => u.user_id));
      const dataroomActiveSet = new Set((dataroomActive || []).map(u => u.user_id));
      const capraiseActiveSet = new Set((capraiseActive || []).map(u => u.user_id));

      // Questions - check if there's a questions table or use user_product_roles
      const questionsTotal = productTotals['questions']?.size || 0;
      const questionsActive = 0; // Would need questions activity table

      return {
        success: true,
        data: {
          equity: {
            active: equityActiveSet.size,
            total: productTotals['equity_manager']?.size || 0
          },
          dataroom: {
            active: dataroomActiveSet.size,
            total: productTotals['dataroom']?.size || 0
          },
          capraise: {
            active: capraiseActiveSet.size,
            total: productTotals['capraise']?.size || 0
          },
          questions: {
            active: questionsActive,
            total: questionsTotal
          }
        }
      };
    } catch (error) {
      console.error('Error fetching feature usage:', error);
      return {
        success: false,
        error: error.message,
        data: {
          equity: { active: 0, total: 0 },
          dataroom: { active: 0, total: 0 },
          capraise: { active: 0, total: 0 },
          questions: { active: 0, total: 0 }
        }
      };
    }
  },

  /**
   * Fetch user journey metrics
   * Returns: signups, activations, activeUsers, retention
   */
  async fetchUserJourney() {
    try {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Signups (users created in last 30 days)
      const { count: signups } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Activations (users who have logged in or used a product in last 30 days)
      // This is users who have any activity
      const [equityActive, dataroomActive] = await Promise.all([
        supabase
          .from('equity_logs')
          .select('user_id')
          .gte('timestamp', thirtyDaysAgo.toISOString()),
        supabase
          .from('dataroom_logs')
          .select('user_id')
          .gte('timestamp', thirtyDaysAgo.toISOString())
      ]);

      const activatedUserIds = new Set([
        ...(equityActive.data || []).map(u => u.user_id),
        ...(dataroomActive.data || []).map(u => u.user_id)
      ]);

      const activations = activatedUserIds.size;

      // Active users (same as activations for now)
      const activeUsers = activations;

      return {
        success: true,
        data: {
          signups: signups || 0,
          activations: activations,
          activeUsers: activeUsers
        }
      };
    } catch (error) {
      console.error('Error fetching user journey:', error);
      return {
        success: false,
        error: error.message,
        data: {
          signups: 0,
          activations: 0,
          activeUsers: 0
        }
      };
    }
  },

  /**
   * Fetch page analytics
   * Returns: top pages, average time per page, page views by module
   */
  async fetchPageAnalytics(days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Check if page_views table exists
      const { data: pageViews, error } = await supabase
        .from('page_views')
        .select('page_path, page_name, module, time_on_page_seconds, viewed_at')
        .gte('viewed_at', startDate.toISOString())
        .order('viewed_at', { ascending: false });

      if (error) {
        // Table doesn't exist yet - return empty data
        return {
          success: true,
          data: {
            topPages: [],
            avgTimePerPage: {},
            pageViewsByModule: {},
            totalPageViews: 0
          }
        };
      }

      // Calculate top pages by view count
      const pageCounts = {};
      const pageTimes = {};
      const moduleCounts = {};

      (pageViews || []).forEach(view => {
        const key = view.page_path || view.page_name || 'unknown';
        
        // Count views
        pageCounts[key] = (pageCounts[key] || 0) + 1;
        
        // Track time on page
        if (view.time_on_page_seconds) {
          if (!pageTimes[key]) {
            pageTimes[key] = [];
          }
          pageTimes[key].push(view.time_on_page_seconds);
        }

        // Count by module
        const module = view.module || 'other';
        moduleCounts[module] = (moduleCounts[module] || 0) + 1;
      });

      // Get top 10 pages
      const topPages = Object.entries(pageCounts)
        .map(([path, count]) => ({
          path,
          name: pageViews.find(v => (v.page_path || v.page_name) === path)?.page_name || path,
          views: count,
          avgTime: pageTimes[path] 
            ? Math.round(pageTimes[path].reduce((a, b) => a + b, 0) / pageTimes[path].length)
            : 0
        }))
        .sort((a, b) => b.views - a.views)
        .slice(0, 10);

      // Calculate average time per page
      const avgTimePerPage = {};
      Object.keys(pageTimes).forEach(path => {
        const times = pageTimes[path];
        avgTimePerPage[path] = Math.round(times.reduce((a, b) => a + b, 0) / times.length);
      });

      return {
        success: true,
        data: {
          topPages,
          avgTimePerPage,
          pageViewsByModule: moduleCounts,
          totalPageViews: pageViews?.length || 0
        }
      };
    } catch (error) {
      console.error('Error fetching page analytics:', error);
      return {
        success: false,
        error: error.message,
        data: {
          topPages: [],
          avgTimePerPage: {},
          pageViewsByModule: {},
          totalPageViews: 0
        }
      };
    }
  }
};

export default UserAnalyticsService;

